/**
 * Validazione del codice fiscale italiano (16 caratteri), con gestione
 * dell'omocodia e del carattere di controllo.
 * Funzione pura, senza dipendenze: è una delle regole con test tabellari.
 */

const ALFABETO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const MESI = 'ABCDEHLMPRST';
const OMOCODIA = 'LMNPQRSTUV'; // L=0 ... V=9

const DISPARI: Record<string, number> = {
  '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21, K: 2, L: 4, M: 18,
  N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};

function valorePari(c: string): number {
  return c >= '0' && c <= '9' ? Number(c) : ALFABETO.indexOf(c);
}

function cifraDaOmocodia(c: string): number {
  if (c >= '0' && c <= '9') return Number(c);
  const i = OMOCODIA.indexOf(c);
  return i >= 0 ? i : -1;
}

export function normalizzaCodiceFiscale(input: string): string {
  return input.replace(/\s+/g, '').toUpperCase();
}

export function carattereDiControllo(primi15: string): string {
  let somma = 0;
  for (let i = 0; i < 15; i++) {
    const c = primi15[i]!;
    somma += i % 2 === 0 ? (DISPARI[c] ?? 0) : valorePari(c);
  }
  return ALFABETO[somma % 26]!;
}

export type EsitoCodiceFiscale =
  | { valido: true; codiceFiscale: string }
  | { valido: false; messaggio: string };

/**
 * Restituisce il CF normalizzato se valido, altrimenti un messaggio nel tono
 * del brand book: dice cosa non va e cosa fare.
 */
export function validaCodiceFiscale(input: string): EsitoCodiceFiscale {
  const cf = normalizzaCodiceFiscale(input);

  if (cf.length !== 16) {
    return {
      valido: false,
      messaggio: `Il codice fiscale ha 16 caratteri: ne hai inseriti ${cf.length}.`,
    };
  }
  if (!/^[A-Z0-9]{16}$/.test(cf)) {
    return { valido: false, messaggio: 'Il codice fiscale può contenere solo lettere e numeri.' };
  }
  // Struttura: 6 lettere, anno (2), mese (1 lettera), giorno (2), comune (1 lettera + 3), controllo
  const struttura = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;
  if (!struttura.test(cf)) {
    return {
      valido: false,
      messaggio: 'Il codice fiscale non ha la struttura attesa: controlla di averlo copiato bene.',
    };
  }
  if (!MESI.includes(cf[8]!)) {
    return { valido: false, messaggio: 'La lettera del mese nel codice fiscale non è valida.' };
  }
  const giorno = cifraDaOmocodia(cf[9]!) * 10 + cifraDaOmocodia(cf[10]!);
  const giornoNormalizzato = giorno > 40 ? giorno - 40 : giorno;
  if (giornoNormalizzato < 1 || giornoNormalizzato > 31) {
    return { valido: false, messaggio: 'Il giorno di nascita nel codice fiscale non è valido.' };
  }
  if (carattereDiControllo(cf.slice(0, 15)) !== cf[15]) {
    return {
      valido: false,
      messaggio:
        "L'ultimo carattere del codice fiscale non corrisponde: controlla di averlo copiato bene.",
    };
  }
  return { valido: true, codiceFiscale: cf };
}

/** Estrae sesso e data di nascita (anno a due cifre, mese, giorno) da un CF valido. */
export function datiDaCodiceFiscale(cf: string): { sesso: 'F' | 'M'; mese: number; giorno: number; anno2: number } {
  const anno2 = cifraDaOmocodia(cf[6]!) * 10 + cifraDaOmocodia(cf[7]!);
  const mese = MESI.indexOf(cf[8]!) + 1;
  const g = cifraDaOmocodia(cf[9]!) * 10 + cifraDaOmocodia(cf[10]!);
  return { sesso: g > 40 ? 'F' : 'M', mese, giorno: g > 40 ? g - 40 : g, anno2 };
}
